import React from 'react';
import {
  Ruler,
  ClipboardList,
  Calendar,
  CheckCircle,
  XCircle as XCircleIcon,
  Tag,
  Layers,
  FileText,
  User,
  UserCheck,
} from 'lucide-react';
import { UoMMaster, User as UserType } from '../../../services/uomMasterService';

interface UoMMasterDetailViewProps {
  uomMaster: UoMMaster | null;
}

const UoMMasterDetailView: React.FC<UoMMasterDetailViewProps> = ({
  uomMaster,
}) => {
  if (!uomMaster) return null;

  const getStatusIcon = (isActive: boolean) => {
    return isActive 
      ? <CheckCircle size={16} className="text-green-600" />
      : <XCircleIcon size={16} className="text-red-600" />;
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? "text-green-600 bg-green-50 border-green-200"
      : "text-red-600 bg-red-50 border-red-200";
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserName = (user: string | UserType): string => {
    if (typeof user === 'object' && user !== null && 'name' in user) {
      return user.name;
    }
    return 'N/A';
  };

  const getUserEmail = (user: string | UserType): string => {
    if (typeof user === 'object' && user !== null && 'email' in user) {
      return user.email || 'N/A';
    }
    return 'N/A';
  };


  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
          <Ruler size={24} className="text-gray-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">
            UOM Details
          </h2>
          <p className="text-sm text-gray-600">
            {uomMaster.name} • {uomMaster.symbol} • {uomMaster.category}
          </p>
        </div>
        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(uomMaster.isActive)}`}>
          {getStatusIcon(uomMaster.isActive)}
          <span className="ml-1.5">{uomMaster.isActive ? 'Active' : 'Inactive'}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="grid grid-cols-1 gap-6">
          {/* UOM Information */}
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <Ruler size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">UOM Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Ruler size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Name</p>
                    <p className="text-sm text-gray-600">{uomMaster.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Tag size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Symbol</p>
                    <p className="text-sm text-gray-600">{uomMaster.symbol}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Layers size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Category</p>
                    <p className="text-sm text-gray-600">{uomMaster.category}</p>
                  </div>
                </div>

                {uomMaster.description && (
                  <div className="flex items-start gap-3">
                    <FileText size={16} className="text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Description</p>
                      <p className="text-sm text-gray-600">{uomMaster.description}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  {getStatusIcon(uomMaster.isActive)}
                  <div>
                    <p className="text-sm font-medium text-gray-900">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(uomMaster.isActive)}`}>
                      {uomMaster.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-4">
                <UserCheck size={20} className="text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">User Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created By</p>
                    <p className="text-sm text-gray-600">{getUserName(uomMaster.createdBy)}</p>
                    {typeof uomMaster.createdBy === 'object' && uomMaster.createdBy?.email && (
                      <p className="text-xs text-gray-500 mt-0.5">{getUserEmail(uomMaster.createdBy)}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <UserCheck size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Updated By</p>
                    <p className="text-sm text-gray-600">{getUserName(uomMaster.updatedBy)}</p>
                    {typeof uomMaster.updatedBy === 'object' && uomMaster.updatedBy?.email && (
                      <p className="text-xs text-gray-500 mt-0.5">{getUserEmail(uomMaster.updatedBy)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamp Information */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={20} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Timestamp Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created At</p>
                    <p className="text-sm text-gray-600">{formatDate(uomMaster.createdAt)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Updated At</p>
                    <p className="text-sm text-gray-600">{formatDate(uomMaster.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UoMMasterDetailView;
